import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from 'lucide-react';

/**
 * Range helper for the Dashboard KPI period filter.
 * Returns ISO start/end (YYYY-MM-DD) inclusive, or null when "all".
 */
export function getDashboardRange(period, customStart, customEnd) {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const lastDay = new Date(yyyy, today.getMonth() + 1, 0).getDate();
  const monthStart = `${yyyy}-${mm}-01`;
  const monthEnd = `${yyyy}-${mm}-${String(lastDay).padStart(2, '0')}`;

  switch (period) {
    case 'thisMonth':
      return { start: monthStart, end: monthEnd, label: `Este mês (${mm}/${yyyy})` };
    case 'last3': {
      const d = new Date(yyyy, today.getMonth() - 2, 1);
      const sy = d.getFullYear();
      const sm = String(d.getMonth() + 1).padStart(2, '0');
      return { start: `${sy}-${sm}-01`, end: monthEnd, label: `Últimos 3 meses` };
    }
    case 'last6': {
      const d = new Date(yyyy, today.getMonth() - 5, 1);
      const sy = d.getFullYear();
      const sm = String(d.getMonth() + 1).padStart(2, '0');
      return { start: `${sy}-${sm}-01`, end: monthEnd, label: `Últimos 6 meses` };
    }
    case 'thisYear':
      return { start: `${yyyy}-01-01`, end: `${yyyy}-12-31`, label: `Ano ${yyyy}` };
    case 'lastYear':
      return { start: `${yyyy - 1}-01-01`, end: `${yyyy - 1}-12-31`, label: `Ano ${yyyy - 1}` };
    case 'custom':
      if (!customStart || !customEnd) return null;
      return { start: customStart, end: customEnd, label: `${customStart} a ${customEnd}` };
    case 'all':
    default:
      return null;
  }
}

function PeriodFilter({ period, onPeriodChange, customStart, customEnd, onCustomStartChange, onCustomEndChange }) {
  const range = getDashboardRange(period, customStart, customEnd);
  return (
    <div className="flex flex-wrap items-end gap-3 p-3 border border-emerald-200 bg-emerald-50/40 rounded-lg">
      <div className="flex items-center gap-2 text-sm text-emerald-800">
        <Calendar className="w-4 h-4" />
        <span className="font-medium">Período:</span>
      </div>
      <Select value={period} onValueChange={onPeriodChange}>
        <SelectTrigger className="w-48 bg-white" data-testid="dashboard-period-filter">
          <SelectValue placeholder="Selecione o período" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="thisMonth">Este mês</SelectItem>
          <SelectItem value="last3">Últimos 3 meses</SelectItem>
          <SelectItem value="last6">Últimos 6 meses</SelectItem>
          <SelectItem value="thisYear">Este ano</SelectItem>
          <SelectItem value="lastYear">Ano anterior</SelectItem>
          <SelectItem value="custom">Personalizado</SelectItem>
          <SelectItem value="all">Tudo (histórico)</SelectItem>
        </SelectContent>
      </Select>

      {period === 'custom' && (
        <>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-emerald-700">De</Label>
            <Input
              type="date"
              value={customStart}
              onChange={(e) => onCustomStartChange(e.target.value)}
              className="w-40 bg-white"
              data-testid="dashboard-custom-start"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-emerald-700">Até</Label>
            <Input
              type="date"
              value={customEnd}
              onChange={(e) => onCustomEndChange(e.target.value)}
              className="w-40 bg-white"
              data-testid="dashboard-custom-end"
            />
          </div>
        </>
      )}

      <div className="flex-1" />

      {range && (
        <Badge variant="outline" className="bg-white border-emerald-300 text-emerald-800" data-testid="dashboard-period-label">
          {range.label}
        </Badge>
      )}
    </div>
  );
}

export default PeriodFilter;
